import React, {
  FC,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import { importRemote } from '@module-federation/utilities';
import PropTypes from 'prop-types';

import {
  REPORT_TYPES,
  TOTAL_REMOTE_LOADING_TIME,
  report,
} from '@adsk/exo-skeleton-api';
import { useRemote } from '@adsk/exo-skeleton-react';

import {
  EXO_SKELETON_SCOPE_NAME,
  RETRY_IMPORT_REMOTE,
  RETRY_IMPORT_REMOTE_FAILURE,
} from '../../consts';
import { createLazyWithRetries } from '../../utils/createLazyWithRetries';
import {
  abortTracking,
  REMOTE_TRACKING_TIMEOUT,
  type PerformanceTrackingAbortReasons,
} from '../../utils/performance';
import { removeFileNameFromURL } from '../../utils/removeFileNameFromURL';
import AppContentLoader from '../AppContentLoader';

const REMOTE_ENTRY_FILENAME = 'remoteEntry.js';

export type LazyComponentProps = {
  url: string;
  module: string;
  scope: string;
  renderLoader?: () => NonNullable<ReactNode> | null;
  children?: ReactNode;
};

const LazyComponent: FC<LazyComponentProps> = React.memo(
  ({
    renderLoader = ({
      scope,
      moduleName,
    }: {
      scope: string;
      moduleName: string;
    }) => <AppContentLoader scope={scope} moduleName={moduleName} />,
    children,
    ...props
  }) => {
    const { url, module, scope } = props;
    const { trackLoading } = useRemote();

    const abortTrackingHandler = useCallback(
      ({ reason }: { reason: PerformanceTrackingAbortReasons }) =>
        abortTracking({
          scope,
          moduleName: module,
          trackingId: TOTAL_REMOTE_LOADING_TIME,
          reason,
        }),
      [scope, module],
    );

    useEffect(() => {
      trackLoading();

      const timeout = setTimeout(() => {
        void abortTrackingHandler({ reason: 'timeout' });
      }, REMOTE_TRACKING_TIMEOUT);

      return () => {
        void abortTrackingHandler({ reason: 'unmount' });
        clearTimeout(timeout);
      };

      // for every scope or module changes, we need to abort the previous tracking and start a new one
    }, [scope, module, abortTrackingHandler, trackLoading]);

    const lazyWithRetries = useCallback(
      (importer) => {
        const sharedReportMetadata = {
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            module,
            scope,
          },
        };

        const execute = createLazyWithRetries({
          onRetry: ({ attemptNumber, rootError }) => {
            return report({
              ...sharedReportMetadata,
              type: REPORT_TYPES.info,
              value: RETRY_IMPORT_REMOTE,
              context: {
                ...sharedReportMetadata.context,
                attemptNumber,
                rootError,
              },
            });
          },
          onRetryFailure: ({ attemptNumber, error, rootError }) => {
            return report({
              ...sharedReportMetadata,
              type: REPORT_TYPES.info,
              value: RETRY_IMPORT_REMOTE_FAILURE,
              context: {
                ...sharedReportMetadata.context,
                attemptNumber,
                error,
                rootError,
              },
            });
          },
        });

        return execute(importer);
      },
      [module, scope],
    );

    const Component = useMemo(
      () =>
        lazyWithRetries(() =>
          importRemote({
            url: removeFileNameFromURL(url, REMOTE_ENTRY_FILENAME),
            module,
            scope,
            bustRemoteEntryCache: false,
            remoteEntryFileName: REMOTE_ENTRY_FILENAME,
          }),
        ),
      [url, module, scope, lazyWithRetries],
    );

    return (
      <Suspense fallback={renderLoader({ scope, moduleName: module })}>
        <Component {...props}>{children}</Component>
      </Suspense>
    );
  },
);

LazyComponent.displayName = 'LazyComponent';
LazyComponent.propTypes = {
  url: PropTypes.string.isRequired,
  module: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  renderLoader: PropTypes.func,
  children: PropTypes.node,
};

export default LazyComponent;
